/**
 * "What moved since last time" — the read side of the append-only history.
 *
 * Renders directly under the exposure score, not on a separate page. ADR-022
 * says the delta over time IS the product; a delta you have to navigate to find
 * is a report, and reports get skipped.
 *
 * The honesty rules live in `utils/delta.js`. This component's only job is to
 * refuse to draw an arrow when that module says the two numbers are not
 * comparable — a green ↓ next to two incomparable measurements is worse than
 * showing nothing, because it is wrong in the direction the user wants to
 * believe.
 */
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN } from "../utils/constants";

const fmtDate = (d) =>
  d ? d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "an earlier session";

// Exposure falling is good news for the person. Down is green here, and that
// inversion is deliberate — see the label, which never says just "↓".
const dirColour = (change) => (change < 0 ? GREEN : change > 0 ? AMBER : CREAM60);
const arrow = (change) => (change < 0 ? "↓" : change > 0 ? "↑" : "→");

export default function Delta({ verdict }) {
  if (!verdict) return null;
  const { comparable, reason, when, overall, axes, priorTitle, currentTitle } = verdict;

  return (
    <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 12,
                  padding: 18, marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: TEAL, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>
        Since {fmtDate(when)}
      </div>

      {!comparable ? (
        <>
          <p style={{ color: CREAM, fontSize: 14, lineHeight: 1.6, margin: "8px 0 0" }}>
            {reason === "different-role" ? (
              <>
                You were assessed as <strong>{priorTitle || "a different role"}</strong> then, and{" "}
                <strong>{currentTitle || "something else"}</strong> now. That is the change worth
                noticing — and it means the two scores measure different jobs, so we are not going
                to subtract one from the other and call the result progress.
              </>
            ) : (
              <>
                The scoring method has changed since that assessment. A shift caused by our own
                model is not a shift in your work, so the two numbers are not compared here.
              </>
            )}
          </p>
          <div style={{ display: "flex", gap: 20, marginTop: 14, flexWrap: "wrap" }}>
            <Stat label="Then" value={overall.from} sub={priorTitle} />
            <Stat label="Now" value={overall.to} sub={currentTitle} />
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
                        marginTop: 8 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: dirColour(overall.change),
                           fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
              {arrow(overall.change)} {Math.abs(overall.change).toFixed(1)}
            </span>
            <span style={{ fontSize: 14, color: CREAM60 }}>
              {overall.change === 0
                ? "overall exposure is unchanged"
                : overall.change < 0
                  ? `overall exposure is lower — ${overall.from} then, ${overall.to} now`
                  : `overall exposure is higher — ${overall.from} then, ${overall.to} now`}
            </span>
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 14 }}>
            {axes.map((a) => (
              <div key={a.key} style={{ minWidth: 96 }}>
                <div style={{ fontSize: 10, color: CREAM30, textTransform: "uppercase",
                              letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>{a.label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: CREAM,
                              fontFamily: "'JetBrains Mono', monospace" }}>
                  {a.to}
                  <span style={{ fontSize: 12, fontWeight: 500, color: CREAM30, marginLeft: 6 }}>
                    {a.change === 0 ? "—" : `${a.change > 0 ? "+" : ""}${a.change}`}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {reason === "different-personalisation" && (
            <p style={{ color: CREAM60, fontSize: 12, lineHeight: 1.6, margin: "14px 0 0" }}>
              One of these two runs used the activities you confirmed and the other used the
              occupation's typical profile. Some of this difference is your own input becoming
              visible, not the work changing underneath you.
            </p>
          )}
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, sub }) => (
  <div>
    <div style={{ fontSize: 10, color: CREAM30, textTransform: "uppercase", letterSpacing: 1,
                  fontFamily: "'JetBrains Mono', monospace" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: CREAM,
                  fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: CREAM60, maxWidth: 200 }}>{sub}</div>}
  </div>
);
